import React, { useMemo, useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import CircularProgress from '@material-ui/core/CircularProgress';
import Star from '@material-ui/icons/Star';
import DiscreteSliderAdmin from "../../user_dashboard/treatmentSession/rehabPhaseProgressBar_admin";
import { CircularProgress as MuiCircularProgress, Box } from '@material-ui/core';
import Searchbar from './patientSearchBar';
import DiagnosisCell from './DiagnosisCell';

const StyledTableRow = styled(TableRow)({
  '& td, & th': {
    paddingTop: 'clamp(2px, calc(-0.5px + 0.3906vw), 5px)',
    paddingBottom: 'clamp(2px, calc(-0.5px + 0.3906vw), 5px)',
    paddingLeft: 'clamp(4px, calc(0px + 0.4167vw), 8px)',
    paddingRight: 'clamp(4px, calc(0px + 0.4167vw), 8px)',
    height: 'clamp(28px, calc(8px + 1.5625vw), 44px)',
  },'&:hover': {
    background: '#FFF',
    //background: 'linear-gradient(90deg, #f5faff 60%, #e9f1f6ff 100%)',
  },
  '&.selected': {
    background: '#FFF',
  }
});

const fmt1 = (v, isPercent = false) => {
  if (v === null || v === undefined) return '-';
  const n = Number(v);
  if (Number.isNaN(n)) return '-';
  const rounded = Math.round(n * 10) / 10;
  return isPercent ? `${rounded}%` : `${rounded}`;
};

// Spinner that shows for up to 2s, then displays 'N/A'
function TimeoutSpinner({ size = 16, timeout = 2000 }) {
  const [expired, setExpired] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setExpired(true), timeout);
    return () => clearTimeout(timer);
  }, [timeout]);
  return expired ? <span style={{ color: '#888', fontSize: 12 }}>{'N/A'}</span> : <CircularProgress size={size} />;
}

const trendColor = (change, isPercent = false, invert = false) => {
  if (change === null || change === undefined) return '#002D55';
  const n = Number(change);
  if (Number.isNaN(n)) return '#002D55';
  const abs = Math.abs(n);
  const threshold = isPercent ? 2 : 1;
  if (abs <= threshold) return '#fbc02d';
  const increased = n > 0;
  const isGood = invert ? increased : !increased;
  return isGood ? '#66bb6a' : '#f14747ff';
};

const normalizeFunctionGoalText = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const getFunctionComparisonTextKey = (comparison) => {
  const followup = normalizeFunctionGoalText(comparison && comparison.followup_text);
  if (followup) return followup;
  return normalizeFunctionGoalText(comparison && comparison.baseline_text);
};

const scoreFunctionComparison = (comparison) => {
  const hasText = !!getFunctionComparisonTextKey(comparison);
  const hasFollowup = comparison && comparison.followup_value !== undefined && comparison.followup_value !== null;
  const hasBaseline = comparison && comparison.baseline_value !== undefined && comparison.baseline_value !== null;
  const hasChange = comparison && (
    (comparison.absolute_change !== undefined && comparison.absolute_change !== null)
    || (comparison.percent_change !== undefined && comparison.percent_change !== null)
    || (comparison.last7_absolute_change !== undefined && comparison.last7_absolute_change !== null)
    || (comparison.last7_percent_change !== undefined && comparison.last7_percent_change !== null)
  );
  return (hasText ? 8 : 0) + (hasFollowup ? 4 : 0) + (hasBaseline ? 2 : 0) + (hasChange ? 1 : 0);
};

const EXTRA_PAIN_TOOLTIP_SUBTYPES = new Set(['rest', 'activity', 'difficulty', 'custom']);

const getPainKpiTooltipHeader = (kpi) => {
  const label = String((kpi && kpi.label) || '').trim();
  if (label) return label;
  const subtype = String((kpi && kpi.kpi_subtype) || '').trim().toLowerCase();
  if (subtype === 'rest') return 'Pain (at rest)';
  if (subtype === 'activity') return 'Pain (with activity)';
  if (subtype === 'difficulty') return 'Difficulty';
  return 'Pain';
};

const buildPainKpiTooltipColumn = (kpi) => {
  if (!kpi || kpi.kpi_type !== 'pain_baseline') return null;
  const subtype = String(kpi.kpi_subtype || '').trim().toLowerCase();
  if (!EXTRA_PAIN_TOOLTIP_SUBTYPES.has(subtype)) return null;
  const hist = Array.isArray(kpi.history) ? kpi.history : [];
  const baselineEntry = hist.length ? hist[0] : null;
  const latestEntry = hist.length ? hist[hist.length - 1] : null;
  const baselineValue = baselineEntry && baselineEntry.value !== undefined && baselineEntry.value !== null
    ? Number(baselineEntry.value)
    : (kpi.numerical_value !== undefined && kpi.numerical_value !== null ? Number(kpi.numerical_value) : null);
  const followupValue = kpi.numerical_value !== undefined && kpi.numerical_value !== null
    ? Number(kpi.numerical_value)
    : (latestEntry && latestEntry.value !== undefined && latestEntry.value !== null ? Number(latestEntry.value) : null);
  const absoluteChange = baselineValue !== null && followupValue !== null ? (followupValue - baselineValue) : null;
  const percentChange = baselineValue !== null && followupValue !== null && baselineValue !== 0
    ? ((followupValue - baselineValue) / baselineValue) * 100
    : (absoluteChange === 0 ? 0 : null);
  return {
    hdr: getPainKpiTooltipHeader(kpi),
    comp: {
      baseline_value: baselineValue,
      followup_value: followupValue,
      absolute_change: absoluteChange,
      percent_change: percentChange,
    },
    key: kpi.id || kpi.question_identifier || `${subtype}:${getPainKpiTooltipHeader(kpi)}`,
  };
};

const extractPainFromBaseline = (baselinePayload) => {
  if (!baselinePayload || !baselinePayload.baselines) return null;
  const pain = baselinePayload.baselines.selfRatedPain;
  if (pain && Array.isArray(pain.items) && pain.items.length) {
    const it = pain.items[0];
    if (it && it.numeric_value !== undefined && it.numeric_value !== null) return it.numeric_value;
    if (it && it.answerId) return parseInt(it.answerId, 10) || null;
  }
  const keys = Object.keys(baselinePayload.baselines || {});
  for (const k of keys) {
    const b = baselinePayload.baselines[k];
    if (b && Array.isArray(b.items) && b.items.length) {
      const it = b.items[0];
      if (it && it.numeric_value !== undefined && it.numeric_value !== null) return it.numeric_value;
      if (it && it.answerId) return parseInt(it.answerId, 10) || null;
    }
  }
  return null;
};

/** helpers to extract function info from payloads */
const extractFunctionFromImprovements = (improvements) => {
  if (!improvements || !Array.isArray(improvements.comparisons)) return null;
  const comp = improvements.comparisons.find(c => {
    const id = (c.question_identifier || '').toLowerCase();
    return id.includes('selfratedfunction') || id.includes('psfs') || id.includes('function');
  });
  if (!comp) return null;
  return {
    baseline_text: comp.baseline_text || null,
    followup_value: (comp.followup_value !== undefined ? comp.followup_value : null),
    percent_change: (comp.percent_change !== undefined ? comp.percent_change : null),
    absolute_change: (comp.absolute_change !== undefined ? comp.absolute_change : null)
  };
};

const extractFunctionFromBaseline = (baselinePayload) => {
  if (!baselinePayload || !baselinePayload.baselines) return null;
  const func = baselinePayload.baselines.selfRatedFunction;
  if (!func || !Array.isArray(func.items) || func.items.length === 0) return null;
  const it = func.items[0];
  return {
    baseline_text: it.text_value || null,
    numeric_value: (it.numeric_value !== undefined ? it.numeric_value : (it.answerId ? parseInt(it.answerId, 10) || null : null))
  };
};

// Extract NPS information from improvements payload. Matches identifiers like 'nps', 'selfrated_nps', 'selfratednps'
const extractNPSFromImprovements = (improvements) => {
  if (!improvements || !Array.isArray(improvements.comparisons)) return null;
  const comp = improvements.comparisons.find(c => {
    const id = (c.question_identifier || '').toLowerCase();
    return id.includes('nps') || id.includes('selfrated_nps') || id.includes('selfratednps') || id.includes('netpromoter');
  });
  if (!comp) return null;
  return {
    baseline_value: (comp.baseline_value !== undefined ? comp.baseline_value : null),
    followup_value: (comp.followup_value !== undefined ? comp.followup_value : null),
    percent_change: (comp.percent_change !== undefined ? comp.percent_change : null),
    absolute_change: (comp.absolute_change !== undefined ? comp.absolute_change : null),
    baseline_text: comp.baseline_text || null,
    followup_text: comp.followup_text || null
  };
};

// Extract NPS from baseline payload. Prefer entries under selfRatedFunction/selfRated_NPS; otherwise scan baseline sections for numeric values in 0-10 range.
const extractNPSFromBaseline = (baselinePayload) => {
  if (!baselinePayload || !baselinePayload.baselines) return null;
  // First try the selfRatedFunction section (NPS may be stored here after mapping)
  const srFunc = baselinePayload.baselines.selfRatedFunction;
  if (srFunc && Array.isArray(srFunc.items) && srFunc.items.length) {
    const it = srFunc.items[0];
    // If the numeric value looks like an NPS (0-10) use it
    const maybe = (it.numeric_value !== undefined && it.numeric_value !== null) ? Number(it.numeric_value) : (it.answerId ? parseInt(it.answerId, 10) : null);
    if (maybe !== null && !Number.isNaN(maybe) && maybe >= 0 && maybe <= 10) {
      return { numeric_value: maybe, baseline_text: it.text_value || null };
    }
  }
  // Otherwise scan all baseline sections for a numeric value that looks like an NPS (0-10)
  const keys = Object.keys(baselinePayload.baselines || {});
  for (const k of keys) {
    const b = baselinePayload.baselines[k];
    if (b && Array.isArray(b.items) && b.items.length) {
      const it = b.items[0];
      const val = (it.numeric_value !== undefined && it.numeric_value !== null) ? Number(it.numeric_value) : (it.answerId ? parseInt(it.answerId, 10) : null);
      if (val !== null && !Number.isNaN(val) && val >= 0 && val <= 10) {
        return { numeric_value: val, baseline_text: it.text_value || null };
      }
    }
  }
  return null;
};

const buildPhaseProgressTooltip = (progressDisplay, phaseName) => {
  if (!progressDisplay) {
    return `Progress: ${phaseName || 'Phase'}`;
  }

  return (
    <div style={{ lineHeight: 1.35 }}>
      <div><strong>{phaseName || progressDisplay.phase_name || 'Phase progress'}</strong></div>
      {progressDisplay.anchor_summary ? <div>{progressDisplay.anchor_summary}</div> : null}
      {Array.isArray(progressDisplay.conditions) && progressDisplay.conditions.length ? (
        progressDisplay.conditions.map((condition, index) => (
          <div key={`${condition.metric_key || 'condition'}-${index}`}>
            {`${condition.label || condition.metric_key}: ${condition.summary || `${condition.actual_display || '-'} / ${condition.threshold_display || '-'}`} ${condition.passed ? 'passed' : 'pending'}`}
          </div>
        ))
      ) : null}
      {Array.isArray(progressDisplay.blocking_reasons) && progressDisplay.blocking_reasons.length ? (
        <div style={{ marginTop: 6 }}><strong>{`Waiting on: ${progressDisplay.blocking_reasons.join('; ')}`}</strong></div>
      ) : null}
    </div>
  );
};

/**
 * PatientTable - Memoized table component for displaying patients
 * Extracted to prevent PatientList state changes from affecting SelectedPatientProgram
 */
function PatientTable({
  profiles,
  selectedPatient,
  searchInput,
  setSearchInput,
  selectedTreatmentByUser,
  patientPhases,
  deriveTreatmentsFromProfile,
  handleSelectProfile,
  handleTreatmentSelect,
  handleNewTreatmentRequested,
  handleEditTreatmentRequested,
  onOpenCreatePatientDialog,
  classes,
}) {
  const displayedProfiles = searchInput.trim().length > 0
    ? profiles
    : (selectedPatient ? [selectedPatient] : profiles);

  const CreateButton = withStyles((theme) => ({
    root: {
      boxShadow: 'none',
      // compensate for scale(0.85) wrapper — values ÷ 0.85 so visual size matches DiagnosisCell addButton
      padding: 'clamp(4px, calc(-0.6px + 0.46vw), 7px)',
      minHeight: 'clamp(28px, calc(9px + 1.47vw), 38px)',
      height: 'clamp(28px, calc(9px + 1.47vw), 38px)',
      minWidth: 'clamp(28px, calc(9px + 1.47vw), 38px)',
      width: 'clamp(28px, calc(9px + 1.47vw), 38px)',
      margin: '0px 5px 0px 0',
      borderRadius: 18,
      backgroundColor: 'transparent',
      border: '1px solid transparent',
      color: '#002D55',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      '& svg': {
        fontSize: 'clamp(18px, calc(6.5px + 0.8vw), 24px)',
        display: 'block'
      },
      '&:hover': {
        backgroundColor: '#fff',
        borderColor: '#FF7F27'
      },
      '&:focus': {
        outline: `2px solid rgba(255,165,0,0.15)`,
      },
    }
  }))(IconButton);

  return (
    <Paper className={classes.root}>
      <TableContainer >
        <Table size="small">
          <TableHead >
            <TableRow className={classes.TableRow}>
              <TableCell className={classes.responsiveText} style={{color: "#002D55"}}>
                <div style={{ display: 'flex', alignItems: 'center', transform: 'scale(0.85)', transformOrigin: 'left center', minWidth: 'clamp(72px, 19vw, 90px)', maxWidth: 'clamp(150px, 25vw, 230px)', marginRight: 'clamp(6px, calc(-3px + 1vw), 8px)'}}>
                  <CreateButton
                    onClick={onOpenCreatePatientDialog}
                    aria-label="create-patient"
                    title="Add a new patient manually"
                  >
                    <AddIcon />
                  </CreateButton>
                  <Searchbar
                    className={classes.orangeFocused}
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    profiles={profiles}
                    selectedPatient={selectedPatient}
                    setSelectedPatient={handleSelectProfile}
                  />
                </div>
              </TableCell>
              <TableCell className={classes.responsiveText} style={{color: "#002D55"}}><b>Treatment</b></TableCell>
              <TableCell className={`${classes.responsiveText} ${classes.hideOnXs}`} style={{color: "#002D55", minWidth: 'clamp(80px, calc(40px + 3.125vw), 120px)'}} align="left"><b>Progress</b></TableCell>
              <TableCell className={`${classes.responsiveText} ${classes.hideOnXs}`} style={{color: "#002D55"}}><b>Compliance</b></TableCell>
              <TableCell className={classes.responsiveText} style={{color: "#002D55"}}><b>Pain</b></TableCell>
              <TableCell className={classes.responsiveText} style={{color: "#002D55"}}><b>Function</b></TableCell>
              <TableCell className={`${classes.responsiveText} ${classes.hideOnXs}`} style={{color: "#002D55"}}><b>NPS</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedProfiles.map((profile) => (
              <StyledTableRow
                key={profile.user_id}
                className={profile.user_id === (selectedPatient?.user_id) ? 'selected' : ''}
                onClick={() => {
                  const selected = profiles.find(p => p.user_id === profile.user_id);
                  handleSelectProfile(selected);
                }}
              >
                <TableCell className={`${classes.responsiveText}`}>
                  <div style={{ fontWeight: 400, maxWidth: 'clamp(120px, 20vw, 180px)', paddingLeft: 'clamp(2px, calc(-2px + 0.8vw), 10px)' }} title={`${profile.first_name} ${profile.last_name}`}>
                    {profile.first_name} {profile.last_name}
                  </div>
                </TableCell>
                <TableCell className={`${classes.responsiveText}`}>
                  {(() => {
                    const treatments = deriveTreatmentsFromProfile(profile);
                    const selTid = selectedTreatmentByUser[profile.user_id] || (treatments.length ? treatments[treatments.length - 1].id : '');
                    return (
                      <DiagnosisCell
                        key={`diag-${profile.user_id}-${selTid || 'none'}`}
                        patientId={profile.user_id}
                        treatments={treatments}
                        selectedTreatmentId={selTid}
                        onTreatmentSelect={(treatment) => handleTreatmentSelect(treatment, profile)}
                        onNewTreatmentRequested={() => handleNewTreatmentRequested(profile)}
                        onEditTreatmentRequested={(treatment) => handleEditTreatmentRequested(profile, treatment)}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell className={`${classes.responsiveText} ${classes.hideOnXs}`}>
                  {(() => {
                    const selTid = selectedTreatmentByUser[profile.user_id] || null;
                    const active = selTid ? profile.diagnosis.find(d => (d.treatment_id || d.id) === selTid) : (profile.diagnosis && profile.diagnosis.length ? profile.diagnosis[profile.diagnosis.length - 1] : null);
                    if (!active) return null;
                    const progressDisplay = active.progress_display || null;
                    const progressValue = progressDisplay && progressDisplay.percent !== undefined ? progressDisplay.percent : (active.progress || 0);
                    const phaseName = active.name || '';
                    const truncatedName = phaseName.length > 7 ? phaseName.substring(0, 7) : phaseName;
                    return (
                      <Tooltip title={buildPhaseProgressTooltip(progressDisplay, phaseName)} arrow interactive classes={{ tooltip: classes.wideTooltip }}>
                        <div key={active.id || selTid} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', gap: 'clamp(4px, calc(0px + 0.4167vw), 8px)' }}>
                          <Box position="relative" display="inline-flex" style={{ width: 'clamp(25px, calc(4px + 1.25vw), 32px)', height: 'clamp(25px, calc(4px + 1.25vw), 32px)', flexShrink: 0 }}>
                            <MuiCircularProgress 
                              variant="determinate" 
                              value={progressValue} 
                              size="100%"
                              thickness={4}
                              style={{ color: '#FF7F27', width: '100%', height: '100%' }}
                            />
                            <Box
                              top={0}
                              left={0}
                              bottom={0}
                              right={0}
                              position="absolute"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <span style={{ fontSize: 'clamp(0.42rem, calc(0.09rem + 0.26vw), 0.58rem)', fontWeight: 600 }}>{Math.round(progressValue || 0)}%</span>
                            </Box>
                          </Box>
                          <span style={{ minWidth: 39, fontSize: 'clamp(0.66rem, calc(0.291rem + 0.549vw), 0.95rem)' }}>{truncatedName}</span>
                        </div>
                      </Tooltip>
                    );
                  })()}
                </TableCell>
                <TableCell className={`${classes.responsiveText} ${classes.hideOnXs}`}>
                  {(() => {
                    const selTid = selectedTreatmentByUser[profile.user_id] || null;
                    const active = selTid ? profile.diagnosis.find(d => (d.treatment_id || d.id) === selTid) : (profile.diagnosis && profile.diagnosis.length ? profile.diagnosis[profile.diagnosis.length - 1] : null);
                    if (!active) return null;
                    const metrics = profile._metrics;
                    const compliance = metrics && metrics.improvements_payload && metrics.improvements_payload.compliance ? metrics.improvements_payload.compliance : null;
                    return (
                      <div key={active.id || selTid} style={{ lineHeight: 1.1 }}>
                        {compliance ? (
                          <>
                            <div className={classes.responsiveText} style={{marginBottom: 2}}><b>{`${compliance.avgCompliance}%`}</b></div>
                            <div className={classes.responsiveText2}>{`7d: ${compliance.avgAdherenceLast7}%`}</div>
                          </>
                        ) : (
                          <div className={classes.responsiveText}>-</div>
                        )}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className={classes.responsiveText}>
                  {(() => {
                    const selTid = selectedTreatmentByUser[profile.user_id] || null;
                    const active = selTid ? profile.diagnosis.find(d => (d.treatment_id || d.id) === selTid) : (profile.diagnosis && profile.diagnosis.length ? profile.diagnosis[profile.diagnosis.length - 1] : null);
                    if (!active) return null;
                    const metrics = profile._metrics;
                    const comps = metrics && metrics.improvements_payload && Array.isArray(metrics.improvements_payload.comparisons) ? metrics.improvements_payload.comparisons : [];

                    const compCurrent = comps.find(c => {
                      const id = (c.question_identifier || '').toLowerCase();
                      if (id.includes('_atbest') || id.includes('_atworst')) return false;
                      return id.includes('current') || id.includes('selfratedpain') || id.includes('pain');
                    }) || null;
                    const compBest = comps.find(c => (c.question_identifier||'').toLowerCase().includes('_atbest') || (c.question_identifier||'').toLowerCase().endsWith('atbest')) || null;
                    const compWorst = comps.find(c => (c.question_identifier||'').toLowerCase().includes('_atworst') || (c.question_identifier||'').toLowerCase().endsWith('atworst')) || null;
                    const compFreq = comps.find(c => {
                      const id = (c.question_identifier || '').toLowerCase();
                      return id.includes('frequency') || id.includes('freq') || (id.includes('symptom') && id.includes('freq'));
                    }) || null;

                    // Prefer follow-up value; fall back to comparison baseline_value, then to the numeric value found in the baseline payload.
                    const baselineFromPayload = (metrics && metrics.baseline_payload) ? extractPainFromBaseline(metrics.baseline_payload) : null;
                    const latestPain = (compCurrent && compCurrent.followup_value !== undefined && compCurrent.followup_value !== null)
                      ? compCurrent.followup_value
                      : (compCurrent && compCurrent.baseline_value !== undefined && compCurrent.baseline_value !== null)
                        ? compCurrent.baseline_value
                        : (baselineFromPayload !== undefined && baselineFromPayload !== null ? baselineFromPayload : null);

                    const kpiItems = (metrics && Array.isArray(metrics.kpi_items)) ? metrics.kpi_items : [];
                    const extraPainColumns = kpiItems
                      .map(buildPainKpiTooltipColumn)
                      .filter(Boolean);
                    const painColumns = [
                      { hdr: 'Pain (current)', comp: compCurrent, key: 'current' },
                      { hdr: 'Pain (at best)', comp: compBest, key: 'best' },
                      { hdr: 'Pain (at worst)', comp: compWorst, key: 'worst' },
                      { hdr: 'Frequency', comp: compFreq, key: 'frequency' },
                      ...extraPainColumns,
                    ];
                    const titleNode = (
                      <div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {painColumns.map((col, idx) => {
                            const c = col.comp;
                            const followupDelta = (c && c.followup_value !== undefined && c.followup_value !== null && c.baseline_value !== undefined && c.baseline_value !== null) ? (Number(c.followup_value) - Number(c.baseline_value)) : null;
                            return (
                              <div key={col.key || idx} style={{ minWidth: 90 }}>
                                <div style={{ fontWeight: 700 }}>{col.hdr}</div>
                                <div style={{ height: 6 }} />
                                <div>baseline: {c && c.baseline_value !== undefined && c.baseline_value !== null ? `${fmt1(c.baseline_value)}${col.hdr === 'Frequency' ? '/7' : '/100'}` : '-'}</div>
                                <div>follow-up: {c && c.followup_value !== undefined && c.followup_value !== null ? <span style={{ color: trendColor(followupDelta, false, false) }}>{`${fmt1(c.followup_value)}${col.hdr === 'Frequency' ? '/7' : '/100'}`}</span> : '-'}</div>
                                <div>abs change: {c && c.absolute_change !== undefined && c.absolute_change !== null ? <span style={{ color: trendColor(c.absolute_change, false, false) }}>{fmt1(c.absolute_change)}</span> : '-'}</div>
                                <div>pct change: {c && c.percent_change !== undefined && c.percent_change !== null ? <span style={{ color: trendColor(c.percent_change, true, false) }}>{fmt1(c.percent_change, true)}</span> : '-'}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );

                    // icon logic: compare followup vs baseline (pain coloring)
                    // Structure mirrors Function logic: explicit branches and percent-change fallback.
                    let iconEl = null;
                    if (compCurrent && compCurrent.followup_value !== undefined && compCurrent.baseline_value !== undefined) {
                      if (compCurrent.followup_value > compCurrent.baseline_value) {
                        // Pain increased -> worse (red up)
                        iconEl = <ArrowUpwardIcon style={{ color: '#d32f2f', fontSize: 16 }} />;
                      } else if (compCurrent.followup_value < compCurrent.baseline_value) {
                        // Pain decreased -> improvement (green down)
                        iconEl = <ArrowDownwardIcon style={{ color: '#66bb6a', fontSize: 16 }} />;
                      } else {
                        iconEl = <RemoveIcon style={{ color: '#ebd805ff', fontSize: 16, border: '1px solid #ebd805ff' }} />;
                      }
                    } else {
                      // Only show fallback icon when percent_change is present; otherwise show no icon (no data)
                      if (compCurrent && compCurrent.percent_change !== undefined && compCurrent.percent_change !== null) {
                        iconEl = (Number(compCurrent.percent_change) >= 0)
                          ? <ArrowUpwardIcon style={{ color: '#d32f2f', fontSize: 18 }} />
                          : <ArrowDownwardIcon style={{ color: '#66bb6a', fontSize: 18 }} />;
                      } else {
                        iconEl = null;
                      }
                    }

                    return (
                      <div key={active.id || selTid} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '-1px 0px -5px 0px' }}>
                        <Tooltip title={titleNode} arrow interactive classes={{ tooltip: classes.wideTooltip }}>
                          <div style={{ minWidth: 40, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {iconEl}
                            <span style={{ fontWeight: 600, color: '#002D55' }}>{latestPain !== null ? `${fmt1(latestPain)}/100` : <TimeoutSpinner size={16} timeout={2000} />}</span>
                          </div>
                        </Tooltip>
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className={classes.responsiveText}>
                  {(() => {
                    const selTid = selectedTreatmentByUser[profile.user_id] || null;
                    const active = selTid ? profile.diagnosis.find(d => (d.treatment_id || d.id) === selTid) : (profile.diagnosis && profile.diagnosis.length ? profile.diagnosis[profile.diagnosis.length - 1] : null);
                    if (!active) return null;
                    const metrics = profile._metrics;
                    let rows = [];
                    // Try to pull function comparison from improvements payload (PSFS / SelfRatedFunction)
                    if (metrics && metrics.improvements_payload && Array.isArray(metrics.improvements_payload.comparisons)) {
                      const comps = metrics.improvements_payload.comparisons;
                      // Collect every function/PSFS-main-goal comparison so multiple
                      // functional goals (e.g. "working at office" + "carrying
                      // shopping bags") each render their own row instead of
                      // collapsing to whichever the backend listed first.
                      const funcComps = comps.filter(c => {
                        const id = (c.question_identifier || '').toLowerCase();
                        if (id.includes('_atbest') || id.includes('_atworst')) return false;
                        // Drop rows with no baseline AND no follow-up — these
                        // are stale canonical placeholders left over from a
                        // goal that was renamed/superseded and would render
                        // as an empty "Function" column on hover.
                        const hasBaseline = c.baseline_value !== undefined && c.baseline_value !== null;
                        const hasFollowup = c.followup_value !== undefined && c.followup_value !== null;
                        if (!hasBaseline && !hasFollowup) return false;
                        if (id.includes('selfratedfunction') || id.includes('function')) return true;
                        if (id.includes('psfs') && (id.includes('function') || id.includes('goal') || id.includes('main'))) return true;
                        return false;
                      });

                      const hasNamedGoalComparison = funcComps.some(c => !!getFunctionComparisonTextKey(c));
                      const filteredFuncComps = funcComps.filter(c => {
                        const hasText = !!getFunctionComparisonTextKey(c);
                        const hasFollowup = c.followup_value !== undefined && c.followup_value !== null;
                        // Drop label-less baseline-only placeholders when a
                        // richer named function-goal comparison exists.
                        if (!hasText && !hasFollowup && hasNamedGoalComparison) return false;
                        return true;
                      });

                      // Deduplicate by logical goal text when available,
                      // otherwise by question_identifier. Keep the richest row
                      // so placeholder baseline-only duplicates do not render.
                      const uniqueFuncComps = [];
                      const seenByKey = new Map();
                      for (const c of filteredFuncComps) {
                        const textKey = getFunctionComparisonTextKey(c);
                        const fallbackKey = (c.question_identifier || '').toLowerCase();
                        const key = textKey ? `text:${textKey}` : `qid:${fallbackKey}`;
                        const prior = seenByKey.get(key);
                        if (!prior) {
                          seenByKey.set(key, c);
                          continue;
                        }
                        if (scoreFunctionComparison(c) > scoreFunctionComparison(prior)) {
                          seenByKey.set(key, c);
                        }
                      }
                      seenByKey.forEach(c => uniqueFuncComps.push(c));

                      uniqueFuncComps.forEach(compFunc => {
                        const pct = compFunc.percent_change;
                        const abs = compFunc.absolute_change;
                        const followup = compFunc.followup_value;
                        const baseline = compFunc.baseline_value;
                        // Display priority: latest follow-up value, otherwise the baseline
                        // (so a brand-new patient with only an assessment still shows their
                        // recorded function score instead of an indefinite spinner / N/A),
                        // otherwise percent change as a last resort.
                        const displayValue = (followup !== null && followup !== undefined)
                          ? `${fmt1(followup)}/100`
                          : (baseline !== null && baseline !== undefined)
                            ? `${fmt1(baseline)}/100`
                            : (pct !== null && pct !== undefined ? fmt1(Math.abs(pct), true) : <TimeoutSpinner size={16} timeout={2000} />);
                        rows.push({
                          label: compFunc.baseline_text || compFunc.followup_text || 'Function',
                          question_identifier: compFunc.question_identifier,
                          value: displayValue,
                          baseline_value: baseline,
                          followup_value: followup,
                          absolute_change: abs,
                          percent_change: pct,
                          last7_absolute_change: compFunc.last7_absolute_change,
                          last7_percent_change: compFunc.last7_percent_change,
                          baseline_text: compFunc.baseline_text,
                          followup_text: compFunc.followup_text
                        });
                      });
                    }

                    if (rows.length === 0) {
                      // No improvements comparison found — try to fall back to baseline payload numeric value
                      const baseFunc = (metrics && metrics.baseline_payload) ? extractFunctionFromBaseline(metrics.baseline_payload) : null;
                      if (baseFunc && (baseFunc.numeric_value !== undefined && baseFunc.numeric_value !== null)) {
                        const val = baseFunc.numeric_value;
                        const titleNode = (
                          <div style={{ lineHeight: 1.3 }}>
                            <div><strong>Function</strong></div>
                            <div>baseline: {fmt1(val)}/100</div>
                            <div style={{ height: 6 }} />
                            <div><strong>Change since start:</strong></div>
                            <div>absolute change: -</div>
                            <div>percent change: -</div>
                            <div style={{ height: 6 }} />
                            <div><strong>7d change:</strong></div>
                            <div>absolute change: -</div>
                            <div>percent change: -</div>
                            <div style={{ height: 6 }} />
                            <div><strong>Goal:</strong></div>
                            <div>{baseFunc.baseline_text || '-'} → -</div>
                          </div>
                        );
                        return (
                          <div key={active.id || selTid} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '-1px 0px -5px 0px' }}>
                            <Tooltip title={titleNode} arrow>
                              <div style={{ minWidth: 40, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontWeight: 600, color: '#002D55' }}>{`${fmt1(val)}/100`}</span>
                              </div>
                            </Tooltip>
                          </div>
                        );
                      }
                      return (
                        <div key={active.id || selTid} style={{ display: 'flex', alignItems: 'center', gap: 0, margin: '-1px 0px -5px 0px' }}>
                          <span style={{ fontWeight: 600, color: '#666', minWidth: 50 }}>-</span>
                        </div>
                      );
                    }

                    return (
                      <div key={active.id || selTid} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '-1px 0px -5px 0px' }}>
                        {(() => {
                          // Prioritize the original/canonical PSFS main goal
                          // (no per-goal label suffix) so the headline value
                          // tracks the patient's first / primary functional
                          // goal. Additional goals appear side-by-side in the
                          // tooltip, mirroring how the Pain column shows
                          // "current / at best / at worst / frequency".
                          const isPrimaryId = (qid) => {
                            const id = (qid || '').toLowerCase();
                            return id === 'psfs_selfratedfunction_maingoal'
                              || id === 'selfratedfunction_maingoal'
                              || (id.includes('selfratedfunction_maingoal') && !id.includes('selfratedfunction_maingoal_'));
                          };
                          const sortedRows = [...rows].sort((a, b) => {
                            const aPrim = isPrimaryId(a.question_identifier) ? 0 : 1;
                            const bPrim = isPrimaryId(b.question_identifier) ? 0 : 1;
                            return aPrim - bPrim;
                          });
                          const primary = sortedRows[0];

                          const titleNode = (
                            <div>
                              <div style={{ display: 'flex', gap: 8 }}>
                                {sortedRows.map((r, idx) => {
                                  const colHdr = r.label && r.label !== 'Function'
                                    ? `Function — ${r.label}`
                                    : 'Function';
                                  // 12px padding between adjacent function-goal columns
                                  const colStyle = {
                                    minWidth: 110,
                                    paddingLeft: idx === 0 ? 0 : 12,
                                    paddingRight: idx === sortedRows.length - 1 ? 0 : 12,
                                    borderLeft: idx === 0 ? 'none' : '1px solid rgba(255,255,255,0.2)',
                                  };
                                  const followupDelta = (r.followup_value !== undefined && r.followup_value !== null
                                    && r.baseline_value !== undefined && r.baseline_value !== null)
                                    ? (Number(r.followup_value) - Number(r.baseline_value))
                                    : null;
                                  return (
                                    <div key={idx} style={colStyle}>
                                      <div style={{ fontWeight: 700 }}>{colHdr}</div>
                                      <div style={{ height: 6 }} />
                                      <div>baseline: {r.baseline_value !== undefined && r.baseline_value !== null ? `${fmt1(r.baseline_value)}/100` : '-'}</div>
                                      <div>follow-up: {r.followup_value !== undefined && r.followup_value !== null ? <span style={{ color: trendColor(followupDelta, false, true) }}>{`${fmt1(r.followup_value)}/100`}</span> : '-'}</div>
                                      <div style={{ height: 6 }} />
                                      <div><strong>Change since start:</strong></div>
                                      <div>abs change: {r.absolute_change !== undefined && r.absolute_change !== null ? <span style={{ color: trendColor(r.absolute_change, false, true) }}>{fmt1(r.absolute_change)}</span> : '-'}</div>
                                      <div>pct change: {r.percent_change !== undefined && r.percent_change !== null ? <span style={{ color: trendColor(r.percent_change, true, true) }}>{fmt1(r.percent_change, true)}</span> : '-'}</div>
                                      <div style={{ height: 6 }} />
                                      <div><strong>7d change:</strong></div>
                                      <div>abs change: {r.last7_absolute_change !== undefined && r.last7_absolute_change !== null ? <span style={{ color: trendColor(r.last7_absolute_change, false, true) }}>{fmt1(r.last7_absolute_change)}</span> : '-'}</div>
                                      <div>pct change: {r.last7_percent_change !== undefined && r.last7_percent_change !== null ? <span style={{ color: trendColor(r.last7_percent_change, true, true) }}>{fmt1(r.last7_percent_change, true)}</span> : '-'}</div>
                                      <div style={{ height: 6 }} />
                                      <div><strong>Goal:</strong></div>
                                      <div>{r.baseline_text || '-'} → {r.followup_text || '-'}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );

                          // Headline icon: function coloring of the primary goal
                          // (up = improvement -> light green, down = worse -> red).
                          let iconEl = null;
                          if (primary.followup_value !== undefined && primary.followup_value !== null
                              && primary.baseline_value !== undefined && primary.baseline_value !== null) {
                            if (primary.followup_value > primary.baseline_value) {
                              iconEl = <ArrowUpwardIcon style={{ color: '#66bb6a', fontSize: 16 }} />;
                            } else if (primary.followup_value < primary.baseline_value) {
                              iconEl = <ArrowDownwardIcon style={{ color: '#d32f2f', fontSize: 16 }} />;
                            } else {
                              iconEl = <RemoveIcon style={{ color: '#ebd805ff', fontSize: 16, border: '1px solid #ebd805ff' }} />;
                            }
                          } else if (primary.percent_change !== undefined && primary.percent_change !== null) {
                            iconEl = primary.percent_change >= 0
                              ? <ArrowUpwardIcon style={{ color: '#66bb6a', fontSize: 18 }} />
                              : <ArrowDownwardIcon style={{ color: '#d32f2f', fontSize: 18 }} />;
                          } else {
                            iconEl = <RemoveIcon style={{ color: '#9e9e9e', fontSize: 16 }} />;
                          }

                          // Suffix when more than one goal exists so the
                          // clinician knows extras are visible on hover.
                          const extraCount = sortedRows.length - 1;
                          return (
                            <Tooltip title={titleNode} arrow interactive classes={{ tooltip: classes.wideTooltip }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                {iconEl}
                                <span style={{ fontWeight: 600, color: '#002D55' }}>{primary.value}</span>
                                {extraCount > 0 && (
                                  <span style={{ fontSize: 11, color: '#666', fontWeight: 500 }}>{`+${extraCount}`}</span>
                                )}
                              </div>
                            </Tooltip>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </TableCell>
                <TableCell className={`${classes.responsiveText} ${classes.hideOnXs}`}>
                  {(() => {
                    const selTid = selectedTreatmentByUser[profile.user_id] || null;
                    const active = selTid ? profile.diagnosis.find(d => (d.treatment_id || d.id) === selTid) : (profile.diagnosis && profile.diagnosis.length ? profile.diagnosis[profile.diagnosis.length - 1] : null);
                    if (!active) return null;
                    const metrics = profile._metrics;
                    const npsImp = metrics && metrics.improvements_payload ? extractNPSFromImprovements(metrics.improvements_payload) : null;
                    const npsBase = metrics && metrics.baseline_payload ? extractNPSFromBaseline(metrics.baseline_payload) : null;

                    // Prefer follow-up stored value; fall back to NPS baseline numeric from improvements,
                    // then to scanned baseline payload.
                    const displayNumeric = (npsImp && npsImp.followup_value !== undefined && npsImp.followup_value !== null)
                      ? npsImp.followup_value
                      : (npsImp && npsImp.baseline_value !== undefined && npsImp.baseline_value !== null)
                        ? npsImp.baseline_value
                        : (npsBase && npsBase.numeric_value !== undefined && npsBase.numeric_value !== null) ? npsBase.numeric_value : null;

                    // Tooltip
                    const titleNode = (
                      <div style={{ lineHeight: 1.3 }}>
                        <div><strong>NPS</strong></div>
                        <div style={{ height: 6 }} />
                        <div>absolute change: {npsImp && npsImp.absolute_change !== undefined && npsImp.absolute_change !== null ? <span style={{ color: trendColor(npsImp.absolute_change, false, true) }}>{fmt1(npsImp.absolute_change)}</span> : '-'}</div>
                        <div>percent change: {npsImp && npsImp.percent_change !== undefined && npsImp.percent_change !== null ? <span style={{ color: trendColor(npsImp.percent_change, true, true) }}>{fmt1(npsImp.percent_change, true)}</span> : '-'}</div>
                      </div>
                    );

                    // Render a colored circle for NPS:
                    // - red if <=4
                    // - yellow if 5-6
                    // - green if >=7
                    // - green + small star emoji if 9-10
                    let iconEl = null;
                    const npsValue = displayNumeric;
                    if (npsValue !== null && npsValue !== undefined && !Number.isNaN(Number(npsValue))) {
                      const v = Number(npsValue);
                      let bgColor = '#66bb6a'; // green default
                      if (v <= 4) bgColor = '#d32f2f';
                      else if (v >= 5 && v <= 6) bgColor = '#fbc02d';
                      else if (v >= 7) bgColor = '#66bb6a';
                      const showStar = (v >= 9 && v <= 10);
                      iconEl = (
                        <span style={{ display: 'inline-block', position: 'relative', width: 18, height: 18 }}>
                          <span style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: bgColor, display: 'block', margin: '0 auto' }} />
                          {showStar ? (
                            <Star style={{ position: 'absolute', top: 4, right: 6, color: '#FFD600', fontSize: 13, transform: 'translate(30%, -30%)' }} />
                          ) : null}
                        </span>
                      );
                    } else {
                      iconEl = null;
                    }

                    return (
                      <div key={active.id || selTid} style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '-1px 0px -5px 0px' }}>
                        <Tooltip title={titleNode} arrow>
                          <div style={{ minWidth: 40, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {iconEl}
                            <span style={{ fontWeight: 600, color: '#002D55' }}>{displayNumeric !== null ? `${fmt1(displayNumeric)}/10` : <TimeoutSpinner size={16} timeout={2000} />}</span>
                          </div>
                        </Tooltip>
                      </div>
                    );
                  })()}
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default React.memo(PatientTable);
